import React, { FC } from 'react';
import { connectDeep } from '../../redux-utils';
import styled from 'styled-components';

import { Link } from 'components/Common';
import App from '../../models/app';
import i18n from 'i18n';
import fsc from 'fsc';
import { AppCard } from './AppCard';
import { fontSemibold } from '../../themes';
import { ClockCard } from './ClockCard';
import { viewAllLimit } from '../../config';
import { AppType, CollectionType } from '../../constants';
import { hidenCollectionsIds } from '../../config';

const Container = styled.div`
  margin-bottom: 15px;
  background: #FFFFFF;
`;

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px;
`;

const CollectionName = styled.div`
  color: #112530;
  font-family: ${fontSemibold};
`;

const CollectionLink = styled(Link)`
  color: #707C83;
`;

const AppContainer = styled.div`
  border-top: 1px solid #E7E9EA;
  border-bottom: 1px solid #E7E9EA;
  padding-left: 10px;
`;

const AppCardContainer = styled.div<any>`
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #E7E9EA')};
`;

interface SmallAppListProps {
  apps: App[];
  collectionId?: string;
  onClick?: (appId: string) => void;
}

export const SmallAppList: FC<SmallAppListProps> = ({ apps, collectionId, onClick }) => (
  <AppContainer>
    {apps.map((app, index) => (
      <AppCardContainer key={index} isLast={index === apps.length - 1}>
        <AppCard
          id={app.id}
          name={app.name}
          rating={app.ratingSummary}
          screenshot={app.isTile? app.banners[0] : app.icon}
          showRatings={app.showRatings}
          isPremium={app.isPremium}
          appType={app.type}
          collectionId={collectionId}
          onClick={() => {
            fsc
              .view('Apps')
              .Tapped('App Preview')
              .withPayload({ appId: app.id })
              .send();

            if (onClick) {
              onClick(app.id);
            }
          }
          }
        />
      </AppCardContainer>
    ))}
  </AppContainer>
);

const ClockCardContainer = styled.div`
  width: 28%;
`;

interface ClockContainerProps {
  hasFooter?: boolean;
}

const ClockContainer = styled.div<ClockContainerProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding-bottom: ${({ hasFooter }) => hasFooter ? 0 : 10}px;
`;

const ClockContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #E7E9EA;
`;

interface SmallClockListProps {
  apps: App[];
  footer?: React.ReactNode;
  collectionId?: string;
  onClick?: ((appId: string) => void);
}

export const SmallClockList: FC<SmallClockListProps> = ({ apps, footer, collectionId, onClick }) => (
  <ClockContentContainer>
    <ClockContainer hasFooter={!!footer}>
      {apps.map((app) => (
        <ClockCardContainer key={app.id}>
          <ClockCard
            id={app.id}
            screenshot={app.banners[0]}
            rating={app.ratingSummary}
            name={app.name}
            showRatings={app.showRatings}
            isPremium={app.isPremium}
            collectionId={collectionId}
            onClick={() => {
              fsc
                .view('Clocks')
                .Tapped('Clock Preview')
                .withPayload({ appId: app.id })
                .send()

              if (onClick) {
                onClick(app.id);
              }
              }
            }
            isInstalled={app.isInstalledOrInstalling}
          />
        </ClockCardContainer>
      ))}
    </ClockContainer>
    {footer}
  </ClockContentContainer>
);

type ReduxProps = ReturnType<typeof mapStateToProps>;

interface CollectionListProps extends ReduxProps {
  name: string;
  apps: App[];
  isClock?: boolean;
  path: string;
  fscViewName: string;
  id: string;
  collectionType?: string;
  inputText?: string;
  shouldDisplayViewAll?: boolean;
  footer?: React.ReactNode;
  appType?: AppType;
  onClickViewAll?: () => void;
}

export const ItemsPreviewComponent: FC<CollectionListProps> = ({
  name,
  isClock,
  path,
  fscViewName,
  apps,
  id,
  shouldDisplayViewAll,
  footer,
  collectionType,
  inputText,
  gregEnabled,
  optimizelyUser,
  onClickViewAll,
}) => {
  const appList = apps.slice(0, 3);
  const displayViewAll = shouldDisplayViewAll ?
    shouldDisplayViewAll : apps.length > viewAllLimit;

  const payload = inputText ? { inputText } : { collectionId: id, collectionType };
  let collectionId: string | undefined;
  if (collectionType && collectionType.toLowerCase() === CollectionType.COLLECTION.toLowerCase()) {
    collectionId = id;
  }

  return (
    <Container>
      <TopContainer>
        <CollectionName>{name}</CollectionName>
        { displayViewAll && <CollectionLink
          to={path}
          pageDirection="FORWARD"
          id={id}
          aria-label={id}
          onClick={() => {
            if (
              hidenCollectionsIds.includes(collectionId || '') &&
              gregEnabled &&
              apps &&
              apps.length > 0 &&
              optimizelyUser
            ) {
              optimizelyUser.trackEvent('greg_collection_open');
            }

            if (onClickViewAll) {
              onClickViewAll();
            }
            fsc
              .view(fscViewName)
              .Tapped('View All')
              .withPayload(payload)
              .send()
            return true;
          }
          }
        >
          {i18n('component.Category.viewAllText')()}
        </CollectionLink> }
      </TopContainer>
      { isClock ?
        <SmallClockList apps={appList} footer={footer} collectionId={collectionId}/> :
        <SmallAppList apps={appList} collectionId={collectionId}/>
      }
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const galleryState = state.gallery;

  const { gregEnabled, userContext } = galleryState.experiments;

  return {
    gregEnabled,
    optimizelyUser: userContext
  }
};

export const ItemsPreview = connectDeep(mapStateToProps)(ItemsPreviewComponent);
